import { Navigate, Outlet, useRoutes } from 'react-router';
// auth
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import MainLayout from '../layouts/main';
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  AboutPage,
  BlankPage,
  Contact,
  EmployeeAppExport,
  EmployeeAppTrackComparisons,
  EmployeeDetail,
  EmployeeList,
  EmployeeMessage,
  EmployeeMessageDetail,
  FaqsPage,
  FileManagerPage,
  GeneralAppPage,
  GlobalExportPage,
  HomePage,
  ImportExcelPage,
  LoginPage,
  MaintenancePage,
  MapPage,
  NewPasswordPage,
  Page403,
  Page404,
  Page500,
  PermissionDeniedPage,
  RegisterPage,
  ResetPasswordPage,
  SchoolCenterDetail,
  SchoolCenterList,
  SchoolConfigEdit,
  SchoolDetail,
  SchoolList,
  TransportationPeriodList,
  TransportationPeriodEditForm,
  SchoolVacationTemplateEditForm,
  SchoolVacationTemplateList,
  StudentDetail,
  StudentList,
  TransportationNeedCheckResultPage,
  TimeMatrixConfig,
  TourDetail,
  TourList,
  TourTransformResultPage,
  TransportCompanyDetail,
  TransportCompanyList,
  VehicleDetail,
  VehicleList,
  StudentApplicationDetail,
  StudentApplicationList,
  VerifyCodePage,
  PlaceManagement,
  TourTenderPage,
  TourPlanningWizardPage,
  RoadblockManagement,
  InterkommunalerLastenausgleichPage,
} from './elements';
import ConfigLotList from '../pages/dashboard/ConfigLotList';
import TourBundleList from '../pages/dashboard/TourBundleList';
import TourBundleDetail from '../pages/dashboard/TourBundleDetail';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <LoginPage /> },
        { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: 'reset-password', element: <ResetPasswordPage /> },
            { path: 'new-password', element: <NewPasswordPage /> },
            { path: 'verify', element: <VerifyCodePage /> },
          ],
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralAppPage /> },
        {
          path: 'schoolcenter',
          children: [
            { element: <Navigate to="/dashboard/schoolcenter/list" replace />, index: true },
            { path: 'list', element: <SchoolCenterList /> },
            { path: ':id', element: <SchoolCenterDetail /> },
            { path: ':id/edit', element: <SchoolCenterDetail /> },
            { path: 'new', element: <SchoolCenterDetail /> },
            { path: ':id/detail', element: <SchoolCenterDetail /> },
          ],
        },
        {
          path: 'school',
          children: [
            { element: <Navigate to="/dashboard/school/list" replace />, index: true },
            { path: 'list', element: <SchoolList /> },
            { path: ':id', element: <SchoolDetail /> },
            { path: 'new', element: <SchoolDetail /> },
            { path: ':id/detail', element: <SchoolDetail /> },
          ],
        },
        {
          path: 'student',
          children: [
            { element: <Navigate to="/dashboard/student/list" replace />, index: true },
            { path: 'list', element: <StudentList /> },
            { path: ':id', element: <StudentDetail /> },
            { path: 'new', element: <StudentDetail /> },
            { path: ':id/detail', element: <StudentDetail /> },
            {
              path: 'transportation-need-check/:id',
              element: <TransportationNeedCheckResultPage />,
            },
          ],
        },
        {
          path: 'tour',
          children: [
            { element: <Navigate to="/dashboard/tour/list" replace />, index: true },
            { path: 'list', element: <TourList tourFetchingType="REAL" /> },
            { path: ':id', element: <TourDetail tourFetchingType="REAL" /> },
            { path: 'new', element: <TourDetail tourFetchingType="REAL" /> },
            { path: ':id/detail', element: <TourDetail tourFetchingType="REAL" /> },
          ],
        },
        {
          path: 'tour-plan',
          children: [
            { element: <Navigate to="/dashboard/tour-plan/list" replace />, index: true },
            { path: 'list', element: <TourList tourFetchingType="PLANNED" /> },
            { path: ':id', element: <TourDetail tourFetchingType="PLANNED" /> },
            { path: 'new', element: <TourDetail tourFetchingType="PLANNED" /> },
            { path: 'transform/:id', element: <TourTransformResultPage /> },
            { path: ':id/detail', element: <TourDetail tourFetchingType="PLANNED" /> },
          ],
        },
        {
          path: 'transportation-company',
          children: [
            {
              element: <Navigate to="/dashboard/transportation-company/list" replace />,
              index: true,
            },
            { path: 'list', element: <TransportCompanyList /> },
            { path: ':id', element: <TransportCompanyDetail /> },
            { path: 'new', element: <TransportCompanyDetail /> },
            { path: ':id/detail', element: <TransportCompanyDetail /> },
          ],
        },
        {
          path: 'employee',
          children: [
            { element: <Navigate to="/dashboard/employee/list" replace />, index: true },
            { path: 'list', element: <EmployeeList /> },
            { path: ':id', element: <EmployeeDetail /> },
            { path: 'new', element: <EmployeeDetail /> },
            { path: ':id/detail', element: <EmployeeDetail /> },
          ],
        },
        {
          path: 'vehicle',
          children: [
            { element: <Navigate to="/dashboard/vehicle/list" replace />, index: true },
            { path: 'list', element: <VehicleList /> },
            { path: ':id', element: <VehicleDetail /> },
            { path: 'new', element: <VehicleDetail /> },
            { path: ':id/detail', element: <VehicleDetail /> },
          ],
        },
        {
          path: 'employee-app',
          children: [
            { path: 'messages', element: <EmployeeMessage /> },
            { path: 'export', element: <EmployeeAppExport /> },
            { path: 'comparison', element: <EmployeeAppTrackComparisons /> },
            { path: 'messages/:id/detail', element: <EmployeeMessageDetail /> },
          ],
        },
        {
          path: 'student-application',
          children: [
            { element: <Navigate to="/dashboard/student-application/list" replace />, index: true },
            { path: 'list', element: <StudentApplicationList /> },
            { path: ':id', element: <StudentApplicationDetail /> },
            { path: ':id/detail', element: <StudentApplicationDetail /> },
          ],
        },
        {
          path: 'basic-setting',
          children: [
            {
              element: (
                <Navigate to="/dashboard/basic-setting/school-vacation-template/list" replace />
              ),
              index: true,
            },
            { path: 'school-vacation-template/list', element: <SchoolVacationTemplateList /> },
            {
              path: 'school-vacation-template/new',
              element: <SchoolVacationTemplateEditForm isEdit={false} />,
            },
            {
              path: 'school-vacation-template/:id/edit',
              element: <SchoolVacationTemplateEditForm isEdit />,
            },
            { path: 'lot-config', element: <ConfigLotList /> },
            {
              path: 'tour-bundle',
              children: [
                {
                  element: <Navigate to="/dashboard/basic-setting/tour-bundle/list" replace />,
                  index: true,
                },
                { path: 'list', element: <TourBundleList /> },
                { path: ':id', element: <TourBundleDetail /> },
                { path: 'new', element: <TourBundleDetail /> },
                { path: ':id/detail', element: <TourBundleDetail /> },
              ],
            },
            { path: 'school-config', element: <SchoolConfigEdit /> },
            {
              path: 'transportation-period',
              children: [
                {
                  element: (
                    <Navigate to="/dashboard/basic-setting/transportation-period/list" replace />
                  ),
                  index: true,
                },
                { path: 'list', element: <TransportationPeriodList /> },
                {
                  path: 'new',
                  element: <TransportationPeriodEditForm isEdit={false} />,
                },
                {
                  path: ':id/detail',
                  element: <TransportationPeriodEditForm isEdit={true} />,
                },
                {
                  path: ':id/edit',
                  element: <TransportationPeriodEditForm isEdit />,
                },
              ],
            },
            { path: 'place-management', element: <PlaceManagement /> },
            { path: 'roadblock-management', element: <RoadblockManagement /> },
            { path: 'excel-import/students', element: <ImportExcelPage entityType="STUDENTS" /> },
            { path: 'excel-import/employees', element: <ImportExcelPage entityType="EMPLOYEES" /> },
            { path: 'excel-import/vehicles', element: <ImportExcelPage entityType="VEHICLES" /> },
            {
              path: 'excel-import/public-transportation',
              element: <ImportExcelPage entityType="PUBLIC_TRANSPORTATION" />,
            },
            { path: 'time-matrix', element: <TimeMatrixConfig /> },
          ],
        },
        { path: 'files-manager', element: <FileManagerPage /> },
        { path: 'export', element: <GlobalExportPage /> },
        { path: 'tour-tender', element: <TourTenderPage /> },
        {
          path: 'interkommunaler-lastenausgleich',
          element: <InterkommunalerLastenausgleichPage />,
        },
        { path: 'permission-denied', element: <PermissionDeniedPage /> },
        { path: 'blank', element: <BlankPage /> },
      ],
    },

    // Main Routes
    {
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'about-us', element: <AboutPage /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <FaqsPage /> },
      ],
    },
    {
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      children: [
        { path: 'map', element: <MapPage /> },
        { path: 'tour-planning-wizard', element: <TourPlanningWizardPage /> },
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
